import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, Users, GraduationCap, BarChart3, Award, Shield, 
  Play, Eye, Settings, FileText, CheckCircle2, ArrowRight, 
  Lock, Zap, BookOpen, Target, Clock, TrendingUp, Download,
  UserCheck, ClipboardCheck, Medal, QrCode, Monitor, Laptop,
  Briefcase, FileSpreadsheet, Phone, Mail, X, Send, Calendar,
  Star, Crown, Sparkles
} from 'lucide-react';

const PRACTIQ_LOGO = "https://customer-assets.emergentagent.com/job_61ca5ff4-bcc2-4cdc-8453-7407936ace18/artifacts/6nklllpo_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

const B2BDemoPage = () => {
  const navigate = useNavigate();
  const [activeDemo, setActiveDemo] = useState('overview');
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [formData, setFormData] = useState({
    instituteName: '',
    studentCount: '',
    contactName: '',
    contactPhone: '',
    contactEmail: ''
  });
  const [demoFormData, setDemoFormData] = useState({
    instituteName: '',
    designation: '',
    studentCount: '',
    contactEmail: '',
    contactPhone: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [demoFormSubmitted, setDemoFormSubmitted] = useState(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/leads/quote-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
    } catch (error) {
      console.log('Quote saved locally:', formData);
    }
    setFormSubmitted(true);
    setTimeout(() => {
      setShowQuoteForm(false);
      setFormSubmitted(false);
      setFormData({ instituteName: '', studentCount: '', contactName: '', contactPhone: '', contactEmail: '' });
    }, 3000);
  };

  const handleDemoFormSubmit = async (e) => {
    e.preventDefault();
    // Save lead data to backend
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/leads/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(demoFormData)
      });
    } catch (error) {
      console.log('Lead saved locally:', demoFormData);
    }
    
    setDemoFormSubmitted(true);
    // Redirect to booking page after 1 second
    setTimeout(() => {
      navigate('/book-demo', { state: { leadData: demoFormData } });
    }, 1500);
  };

  const features = [
    {
      icon: Monitor,
      title: 'Virtual Tax Lab',
      description: 'Exact replica of GST Portal. Make students "Job Ready" from Day 1 with hands-on practice.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Target,
      title: 'Assessment Engine',
      description: 'Exam Mode with field-level accuracy tracking. Generate Employability Scorecards.',
      color: 'from-amber-600 to-amber-700'
    },
    {
      icon: BarChart3,
      title: 'Faculty Dashboard',
      description: 'Track batch performance, individual accuracy, and generate certification reports.',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: Building2,
      title: 'White-Label Branding',
      description: 'Your institute logo alongside PractiQ. Custom branded portal for your students.',
      color: 'from-purple-500 to-indigo-500'
    }
  ];

  const gstModules = [
    {
      name: 'GST Registration',
      description: 'Complete registration simulation with PAN verification and TRN generation',
      included: 'all',
      icon: UserCheck
    },
    {
      name: 'GSTR-1',
      description: 'Outward supplies return with B2B, B2C, exports, and amendments',
      included: 'all',
      icon: FileText
    },
    {
      name: 'GSTR-3B',
      description: 'Monthly summary return with ITC computation and tax payment',
      included: 'all',
      icon: FileSpreadsheet
    },
    {
      name: 'GST Reconciliation',
      description: 'GSTR-2A/2B vs Books reconciliation for ITC validation',
      included: 'pro',
      icon: Target,
      badge: 'Pro'
    },
    {
      name: 'GSTR-9',
      description: 'Annual Return - Comprehensive yearly consolidation',
      included: 'pro',
      icon: Crown,
      badge: 'Pro'
    },
    {
      name: 'GSTR-9C',
      description: 'Reconciliation Statement - Audit-ready compliance',
      included: 'pro',
      icon: Star,
      badge: 'Pro'
    }
  ];

  // Quote Request Modal
  const QuoteModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowQuoteForm(false)} />
      
      <div className="relative bg-[#0F1629] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="relative bg-amber-950/30 px-6 py-5 rounded-t-2xl border-b border-amber-900/20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-700 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Request Institutional Quote</h2>
              <p className="text-sm text-gray-400">Get customized pricing for your institution</p>
            </div>
          </div>
          <button onClick={() => setShowQuoteForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {formSubmitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Request Submitted!</h3>
              <p className="text-gray-400">Our team will contact you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Institute Name *</Label>
                <Input
                  required
                  placeholder="e.g., Delhi Commerce College"
                  value={formData.instituteName}
                  onChange={(e) => setFormData({...formData, instituteName: e.target.value})}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Expected Student Count *</Label>
                <Input
                  required
                  type="number"
                  placeholder="e.g., 200"
                  value={formData.studentCount}
                  onChange={(e) => setFormData({...formData, studentCount: e.target.value})}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Faculty Contact Name *</Label>
                <Input
                  required
                  placeholder="e.g., Dr. Sharma"
                  value={formData.contactName}
                  onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Phone Number *</Label>
                  <Input
                    required
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Email *</Label>
                  <Input
                    required
                    type="email"
                    placeholder="faculty@institute.edu"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-12 bg-amber-600 hover:bg-amber-500 text-white font-semibold mt-4">
                <Send className="w-4 h-4 mr-2" />
                Submit Request
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  // Demo Lead Capture Modal
  const DemoModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowDemoForm(false)} />
      
      <div className="relative bg-[#0F1629] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="relative bg-teal-950/30 px-6 py-5 rounded-t-2xl border-b border-teal-900/20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-teal-700 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Schedule a Demo Call</h2>
              <p className="text-sm text-gray-400">Step 1 of 2: Tell us about your institution</p>
            </div>
          </div>
          <button onClick={() => setShowDemoForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {demoFormSubmitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-teal-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Details Saved!</h3>
              <p className="text-gray-400">Redirecting to booking calendar...</p>
            </div>
          ) : (
            <form onSubmit={handleDemoFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Institute Name *</Label>
                <Input
                  required
                  placeholder="e.g., Delhi Commerce College"
                  value={demoFormData.instituteName}
                  onChange={(e) => setDemoFormData({...demoFormData, instituteName: e.target.value})}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Your Designation *</Label>
                <Input
                  required
                  placeholder="e.g., Head of Commerce Department"
                  value={demoFormData.designation}
                  onChange={(e) => setDemoFormData({...demoFormData, designation: e.target.value})}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Expected Student Count *</Label>
                <Input
                  required
                  type="number"
                  placeholder="e.g., 200"
                  value={demoFormData.studentCount}
                  onChange={(e) => setDemoFormData({...demoFormData, studentCount: e.target.value})}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Email *</Label>
                  <Input
                    required
                    type="email"
                    placeholder="faculty@institute.edu"
                    value={demoFormData.contactEmail}
                    onChange={(e) => setDemoFormData({...demoFormData, contactEmail: e.target.value})}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Phone *</Label>
                  <Input
                    required
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={demoFormData.contactPhone}
                    onChange={(e) => setDemoFormData({...demoFormData, contactPhone: e.target.value})}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-12 bg-teal-600 hover:bg-teal-500 text-white font-semibold mt-4">
                <Calendar className="w-4 h-4 mr-2" />
                Continue to Book Slot
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      {/* Quote Modal */}
      {showQuoteForm && <QuoteModal />}
      
      {/* Demo Lead Capture Modal */}
      {showDemoForm && <DemoModal />}

      {/* Hero Section */}
      <section className="w-full bg-gradient-to-b from-[#0A0F1C] to-[#111827] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-amber-600/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-600/5 rounded-full blur-3xl"></div>
        </div>

        <div className="w-full px-6 lg:px-16 xl:px-24 py-16 relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-amber-900/30 text-amber-300 border-amber-700/30 mb-4 px-4 py-1.5">
              <Building2 className="w-4 h-4 mr-2" />
              Virtual Tax Compliance Lab for Educational Institutions
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="text-white">PractiQ</span>
              <span className="text-amber-400"> Pro</span>
            </h1>
            
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-4">
              Make your students <span className="text-white font-semibold">"Job Ready"</span> from Day 1 with India's most advanced GST simulation platform.
            </p>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-8">
              Trusted by <span className="text-teal-400">CA Coaching Institutes</span>, <span className="text-teal-400">Commerce Colleges</span>, and <span className="text-teal-400">Universities</span>.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button 
                size="lg"
                className="h-14 px-8 bg-amber-600 hover:bg-amber-500 text-white text-lg font-semibold"
                onClick={() => setShowQuoteForm(true)}
              >
                <Building2 className="w-5 h-5 mr-2" />
                Request Institutional Quote
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="h-14 px-8 border-white/20 text-white hover:bg-white/5 text-lg"
                onClick={() => setShowDemoForm(true)}
              >
                <Calendar className="w-5 h-5 mr-2" />
                Schedule a Demo Call
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { value: '50+', label: 'Partner Institutes' },
              { value: '10,000+', label: 'Students Trained' },
              { value: '95%', label: 'Employability Rate' },
              { value: '4.8/5', label: 'Faculty Rating' }
            ].map((stat, i) => (
              <div key={i} className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="w-full bg-[#080C16] py-16 border-t border-white/5">
        <div className="w-full px-6 lg:px-16 xl:px-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why Institutions Choose PractiQ Pro</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Transform theoretical knowledge into practical expertise
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="bg-[#111827] border-white/10 hover:border-white/20 transition-all group">
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-400">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* GST Modules Section */}
      <section className="w-full bg-[#0A0F1C] py-16 border-t border-white/5">
        <div className="w-full px-6 lg:px-16 xl:px-24">
          <div className="text-center mb-12">
            <Badge className="bg-teal-500/10 text-teal-400 border-teal-500/20 mb-4">
              <FileText className="w-4 h-4 mr-2" />
              Comprehensive GST Training
            </Badge>
            <h2 className="text-3xl font-bold text-white mb-4">Complete GST Simulation Suite</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From basic registration to advanced annual returns - everything your students need
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {gstModules.map((module, i) => (
              <div 
                key={i} 
                className={`p-6 rounded-xl border transition-all ${
                  module.included === 'pro' 
                    ? 'bg-amber-950/20 border-amber-800/30' 
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    module.included === 'pro' 
                      ? 'bg-amber-800/50' 
                      : 'bg-teal-500/20'
                  }`}>
                    <module.icon className={`w-6 h-6 ${module.included === 'pro' ? 'text-amber-400' : 'text-teal-400'}`} />
                  </div>
                  {module.badge && (
                    <Badge className="bg-amber-900/40 text-amber-400 border-amber-700/30">
                      <Crown className="w-3 h-3 mr-1" />
                      {module.badge}
                    </Badge>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{module.name}</h3>
                <p className="text-sm text-gray-400">{module.description}</p>
                {module.included === 'pro' && (
                  <p className="text-xs text-amber-500/70 mt-3">Exclusive to Institutional Plans</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section id="demo-section" className="w-full bg-[#080C16] py-16 border-t border-white/5">
        <div className="w-full px-6 lg:px-16 xl:px-24">
          <div className="text-center mb-12">
            <Badge className="bg-teal-500/10 text-teal-400 border-teal-500/20 mb-4">
              <Eye className="w-4 h-4 mr-2" />
              Interactive Demo
            </Badge>
            <h2 className="text-3xl font-bold text-white mb-4">See How It Works</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Experience the platform from both Faculty and Student perspectives
            </p>
          </div>

          <Tabs defaultValue="overview" className="w-full" onValueChange={setActiveDemo}>
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-4 bg-[#111827] border border-white/10 mb-8">
              <TabsTrigger value="overview" className="data-[state=active]:bg-amber-900/30 data-[state=active]:text-amber-400">
                Overview
              </TabsTrigger>
              <TabsTrigger value="admin" className="data-[state=active]:bg-amber-900/30 data-[state=active]:text-amber-400">
                Faculty Portal
              </TabsTrigger>
              <TabsTrigger value="student" className="data-[state=active]:bg-amber-900/30 data-[state=active]:text-amber-400">
                Student View
              </TabsTrigger>
              <TabsTrigger value="certificate" className="data-[state=active]:bg-amber-900/30 data-[state=active]:text-amber-400">
                Scorecards
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-0">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">Complete Workflow</h3>
                  <div className="space-y-4">
                    {[
                      { step: '1', title: 'Institute Onboarding', desc: 'Quick setup with your branding and student list upload' },
                      { step: '2', title: 'Student Access', desc: 'Students login to branded portal with auto-generated credentials' },
                      { step: '3', title: 'Virtual Lab Practice', desc: 'Students complete GST simulations with real portal experience' },
                      { step: '4', title: 'Faculty Review', desc: 'Real-time dashboard showing progress and accuracy scores' },
                      { step: '5', title: 'Employability Scorecard', desc: 'Auto-generated skill reports and verified certificates' }
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 rounded-lg bg-white/5 border border-white/10">
                        <div className="w-8 h-8 rounded-full bg-amber-700 flex items-center justify-center text-white font-bold flex-shrink-0">
                          {item.step}
                        </div>
                        <div>
                          <h4 className="font-medium text-white">{item.title}</h4>
                          <p className="text-sm text-gray-400">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#111827] rounded-2xl border border-white/10 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <img src={PRACTIQ_LOGO} alt="PractiQ" className="w-10 h-10" />
                    <span className="text-2xl text-gray-600">×</span>
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xl font-semibold text-white">Your Institute</span>
                  </div>
                  
                  <div className="aspect-video bg-[#0A0F1C] rounded-xl border border-white/10 flex items-center justify-center mb-4">
                    <div className="text-center">
                      <Monitor className="w-16 h-16 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-500">Co-branded Portal Preview</p>
                      <Button 
                        size="sm" 
                        className="mt-3 bg-amber-600 hover:bg-amber-500"
                        onClick={() => navigate('/demo-institute')}
                      >
                        <Play className="w-4 h-4 mr-1" /> View Live Demo
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Admin Portal Tab */}
            <TabsContent value="admin" className="mt-0">
              <div className="bg-[#111827] rounded-2xl border border-white/10 overflow-hidden">
                <div className="bg-[#0A0F1C] border-b border-white/10 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <img src={PRACTIQ_LOGO} alt="PractiQ" className="w-8 h-8" />
                      <span className="text-lg font-semibold text-white">Faculty Dashboard</span>
                    </div>
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                      <Shield className="w-3 h-3 mr-1" /> Admin Access
                    </Badge>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid md:grid-cols-4 gap-4 mb-6">
                    {[
                      { label: 'Total Students', value: '156', icon: Users, color: 'text-blue-400' },
                      { label: 'Active Sessions', value: '23', icon: Monitor, color: 'text-emerald-400' },
                      { label: 'Avg. Accuracy', value: '87.5%', icon: Target, color: 'text-amber-400' },
                      { label: 'Scorecards Generated', value: '89', icon: Award, color: 'text-purple-400' }
                    ].map((stat, i) => (
                      <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-400">{stat.label}</span>
                          <stat.icon className={`w-5 h-5 ${stat.color}`} />
                        </div>
                        <div className="text-2xl font-bold text-white">{stat.value}</div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                      <h4 className="font-medium text-white">Batch Performance Overview</h4>
                      <Button size="sm" variant="outline" className="border-white/20 text-gray-300">
                        <Download className="w-4 h-4 mr-1" /> Export Report
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-white/5">
                          <tr className="text-left text-sm text-gray-400">
                            <th className="px-4 py-3">Student</th>
                            <th className="px-4 py-3">GSTR-1</th>
                            <th className="px-4 py-3">GSTR-3B</th>
                            <th className="px-4 py-3">Overall Accuracy</th>
                            <th className="px-4 py-3">Employability</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { name: 'Rahul Sharma', gstr1: '92%', gstr3b: '88%', overall: '90%', employability: 'Job Ready' },
                            { name: 'Priya Patel', gstr1: '95%', gstr3b: '91%', overall: '93%', employability: 'Job Ready' },
                            { name: 'Amit Kumar', gstr1: '78%', gstr3b: '82%', overall: '80%', employability: 'Needs Practice' },
                            { name: 'Sneha Gupta', gstr1: '88%', gstr3b: '85%', overall: '86%', employability: 'Job Ready' }
                          ].map((student, i) => (
                            <tr key={i} className="border-t border-white/5 text-sm">
                              <td className="px-4 py-3 text-white">{student.name}</td>
                              <td className="px-4 py-3">
                                <span className={parseInt(student.gstr1) >= 90 ? 'text-emerald-400' : parseInt(student.gstr1) >= 80 ? 'text-amber-400' : 'text-red-400'}>
                                  {student.gstr1}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={parseInt(student.gstr3b) >= 90 ? 'text-emerald-400' : parseInt(student.gstr3b) >= 80 ? 'text-amber-400' : 'text-red-400'}>
                                  {student.gstr3b}
                                </span>
                              </td>
                              <td className="px-4 py-3 font-medium text-white">{student.overall}</td>
                              <td className="px-4 py-3">
                                <Badge className={
                                  student.employability === 'Job Ready' 
                                    ? 'bg-emerald-500/20 text-emerald-400 border-0' 
                                    : 'bg-amber-500/20 text-amber-400 border-0'
                                }>
                                  {student.employability}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Student View Tab */}
            <TabsContent value="student" className="mt-0">
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Practice Mode */}
                <div className="bg-[#111827] rounded-2xl border border-white/10 overflow-hidden">
                  <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-6 py-3">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-emerald-400" />
                      <span className="font-medium text-emerald-400">Practice Mode</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-400 mb-4">Students can practice with hints and guidance enabled</p>
                    <div className="space-y-3">
                      {['Real GST Portal Interface', 'Tooltips & Field Descriptions', 'Error Highlighting', 'Unlimited Retries'].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          <span className="text-gray-300">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Exam Mode */}
                <div className="bg-[#111827] rounded-2xl border border-white/10 overflow-hidden">
                  <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 py-3">
                    <div className="flex items-center gap-2">
                      <ClipboardCheck className="w-5 h-5 text-amber-400" />
                      <span className="font-medium text-amber-400">Assessment Mode</span>
                      <Badge className="bg-red-500/20 text-red-400 border-0 text-xs ml-auto">Faculty Enabled</Badge>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-400 mb-4">Formal assessment with accuracy tracking</p>
                    <div className="space-y-3">
                      {['No Hints or Tooltips', 'Field-level Accuracy Tracking', 'Timed Assessment', 'Employability Score Generation'].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <Lock className="w-4 h-4 text-amber-400" />
                          <span className="text-gray-300">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Scorecard Tab */}
            <TabsContent value="certificate" className="mt-0">
              <div className="max-w-3xl mx-auto">
                <div className="bg-gradient-to-br from-[#1a1f2e] to-[#111827] rounded-2xl border border-amber-900/30 p-8 relative overflow-hidden">
                  <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '20px 20px'}}></div>
                  </div>

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                        <img src={PRACTIQ_LOGO} alt="PractiQ" className="w-12 h-12" />
                        <div>
                          <p className="text-xs text-gray-500">Verified by</p>
                          <p className="font-semibold text-white">PractiQ | TaxSetu AI</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Partner Institute</p>
                          <p className="font-semibold text-white">Demo University</p>
                        </div>
                      </div>
                    </div>

                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold text-amber-400 mb-2">Employability Scorecard</h3>
                      <p className="text-gray-400">GST Compliance Skills Assessment</p>
                    </div>

                    <div className="text-center mb-8">
                      <p className="text-gray-400 mb-2">This certifies that</p>
                      <p className="text-2xl font-semibold text-white mb-4">Rahul Sharma</p>
                      
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="p-4 rounded-lg bg-white/5">
                          <p className="text-xs text-gray-500 mb-1">GSTR-1 Accuracy</p>
                          <p className="text-2xl font-bold text-emerald-400">92%</p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/5">
                          <p className="text-xs text-gray-500 mb-1">GSTR-3B Accuracy</p>
                          <p className="text-2xl font-bold text-emerald-400">88%</p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/5">
                          <p className="text-xs text-gray-500 mb-1">Overall Score</p>
                          <p className="text-2xl font-bold text-amber-400">90%</p>
                        </div>
                      </div>

                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-lg px-4 py-2">
                        <Briefcase className="w-4 h-4 mr-2" />
                        JOB READY
                      </Badge>
                    </div>

                    <div className="flex items-end justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-20 bg-white rounded-lg p-2">
                          <QrCode className="w-full h-full text-gray-800" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Scan to Verify</p>
                          <p className="text-xs text-teal-400">practiq.taxsetu.ai/verify</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Certificate ID</p>
                        <p className="font-mono text-sm text-white">PQ-2025-GST-00892</p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute top-0 left-0 w-full h-1 bg-amber-700"></div>
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-amber-700"></div>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-gray-400 mb-4">Scorecards include QR verification and can be validated by employers</p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full bg-[#0d1220] py-16 border-t border-white/5">
        <div className="w-full px-6 lg:px-16 xl:px-24 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Transform Your Tax Training?</h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Join leading institutions already using PractiQ Pro to produce job-ready tax professionals.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button 
              size="lg"
              className="h-14 px-8 bg-amber-600 hover:bg-amber-500 text-white text-lg font-semibold"
              onClick={() => setShowQuoteForm(true)}
            >
              <Building2 className="w-5 h-5 mr-2" />
              Request Institutional Quote
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="h-14 px-8 border-white/30 text-white hover:bg-white/10"
              onClick={() => navigate('/demo-institute')}
            >
              <Play className="w-5 h-5 mr-2" />
              Try Live Demo
            </Button>
          </div>
          <p className="text-base text-gray-400 mt-6">
            Contact: <span className="text-white font-medium">support@taxsetu.ai</span>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default B2BDemoPage;
